import {isAfter, subWeeks} from 'date-fns';
import itiriri from 'itiriri';
import {
  HSPatchObservation,
  HSPatient,
  HSPackedPatientDay,
  HSDrug,
  HSPackedMedication, 
} from 'server-openapi';
import { DateUtils } from '../../../../core/utils/dateUtils';
import { DrugUtils } from '../../../../syncstream/utils/DrugUtils';
import { PatientUtils } from '../../../../syncstream/utils/PatientUtils';
import { ResidentDetailsUtils } from '../../../../syncstream/utils/ResidentDetailsUtils';
import { PatchStatus } from '../MedicationListsTabbedRouter/TabLists/PatchesMedicationList';
import { PatchLocationDTO, PatchLocations } from './PatchLocations';
import { User } from '../../../../core/authn/user-management';

export const patchRemovalIdentifier = 'REMOVE PATCH';

export interface PatchOperationData {
  packedMedicationId: number;
  patchStatus: PatchStatus;
  patchTime: string;
}

export const PatchUtils = {
  isPatch: (drug?: HSDrug) => {
    return !!drug && (drug.formAbbreviation === 'PTCH' || PatchUtils.isPatchRemoval(drug));
  },
  isPatchRemoval: (drug?: HSDrug) => {
    return ((!!drug?.description && drug.description.endsWith(patchRemovalIdentifier)) ||
            (!!drug?.name && drug.name.endsWith(patchRemovalIdentifier)));
  },
  getRecentPatchLocations: (patientUtils: PatientUtils, patient: HSPatient): (PatchLocationDTO | undefined)[] => {
    const patientAdministeredDoses = patientUtils.getAdministeredDoses(patient.hsId!).map((v) => v.administeredDose);

    const recentPatientPatches = patientAdministeredDoses
      .flatMap((d) => (d.administeredDrugs && d.administeredDrugs.length > 0 ? d.administeredDrugs : []))
      .filter((d) => d.patchLocationNumber && DateUtils.toDate(d.administeredAt!) >= subWeeks(new Date(), 4))
      .sort((a, b) => DateUtils.compareDateStringsDescending(a.administeredAt, b.administeredAt))
      .slice(0, 3);
    const locationMap = new Map<string, PatchLocationDTO>();
    for(const ad of recentPatientPatches) {
      if (!!ad.patchLocationNumber && !locationMap.has(ad.patchLocationNumber.toString())) {
        const loc = PatchLocations.find(pl => pl.LocationNumber === ad.patchLocationNumber);
        if (!!loc) {
          locationMap.set(ad.patchLocationNumber.toString(), loc);
        }
      }
    }

    // Merge in the patch locations.
    const patchApplications = patientUtils.getPatchApplications(patient.hsId!)
        .filter((pa) => pa.lastAppliedAt && DateUtils.toDate(pa.lastAppliedAt) >= subWeeks(new Date(), 4));
    for (const pa of patchApplications) {
      if (!!pa.patchLocation?.number && !locationMap.has(pa.patchLocation.number)) {
        const loc = PatchLocations.find(pl => !!pa.patchLocation?.number && pl.LocationNumber.toString() === pa.patchLocation.number);
        if (!!loc) {
          locationMap.set(pa.patchLocation.number, loc);
        }
      } 
    }
    return [...locationMap.values()];
  },
  getPatchRemovalPackedMedicationForPackedMedication: (
    packedPatientDays: HSPackedPatientDay[],
    drug: HSDrug,
    med: HSPackedMedication,
    patientId: number,
  ) => {
    if (!med.doseTimestamp) {
      return undefined;
    }
    const medTime = DateUtils.toDate(med.doseTimestamp);

    const packedDays = packedPatientDays.filter((day) => day.patientId === patientId);

    const nextPatch = packedDays
      .flatMap((day) => day.packedMedications)
      .filter(
        (packedMed) =>
          med?.medicationId === packedMed?.medicationId &&
          packedMed?.doseTimestamp &&
          DateUtils.toDate(packedMed.doseTimestamp).valueOf() > medTime.valueOf(),
      )
      .sort((a, b) => DateUtils.compareDateStringsDescending(a?.doseTimestamp, b?.doseTimestamp, true))[0];

    return packedDays
      .flatMap((p) => p.packedMedications!)
      .filter((pm) => pm.drugHsId === drug!.patchRemovalDrugId)
      .sort((a, b) => DateUtils.compareDateStringsDescending(a.doseTimestamp, b.doseTimestamp, true))
      .find(
        (removal) =>
          removal.doseTimestamp &&
          DateUtils.toDate(removal.doseTimestamp).valueOf() > medTime.valueOf() &&
          (!nextPatch?.doseTimestamp ||
            DateUtils.toDate(removal.doseTimestamp).valueOf() <= DateUtils.toDate(nextPatch.doseTimestamp).valueOf()),
      );
  }
};
